// Packages
import { defineStore } from 'pinia';

// Logic
import { api } from '@global-client/logic/core/api';

import { createLogger } from '@hub-client/logic/logging/Logger';

// Models
import { Hub } from '@global-client/models/Hubs';

// Stores
import { useHubs } from '@global-client/stores/hubs';
import { useMSS } from '@global-client/stores/mss';

import { Theme, TimeFormat, useSettings } from '@hub-client/stores/settings';

// Types
type PinnedHub = {
	hubId: string;
	hubName: string;
	accessToken?: string;
	userId?: string;
};

type PinnedHubs = Array<PinnedHub>;

interface GlobalSettings {
	theme: Theme;
	timeformat: TimeFormat;
	language: string;
	hubs: PinnedHubs;
}

const defaultGlobalSettings = {
	theme: Theme.System,
	timeformat: TimeFormat.format24,
	language: 'nl', // Default language is set in `hub-client/src/i18n.ts`.
	hubs: [] as PinnedHubs,
};

const logger = createLogger('Global');

const useGlobal = defineStore('global', {
	state: () => {
		return {
			loggedIn: false,
			modalVisible: false,
			contextMenuModalVisible: false,
			// Mirrors the active hub's back state (MessageType.BackState): the hub closes a sidebar or
			// an open forum post itself, so a back swipe must not scroll the hub out of view instead.
			// Defaults to false, so a hub that never reports leaves the scroll behaviour as it was.
			hubCanGoBack: false,
			pinnedHubs: [] as PinnedHubs,
			hubsLoading: false,
		};
	},

	getters: {
		isModalVisible(state): boolean {
			return state.modalVisible || state.contextMenuModalVisible;
		},

		getGlobalSettings(state): GlobalSettings {
			const settings = useSettings();
			const globalSettings: GlobalSettings = {
				theme: settings.theme,
				timeformat: settings.getTimeFormat,
				language: settings.getActiveLanguage,
				hubs: state.pinnedHubs,
			};
			return globalSettings;
		},

		hasPinnedHubs(state): boolean {
			if (!state.pinnedHubs) return false;
			return state.pinnedHubs.length > 0;
		},
	},

	actions: {
		/**
		 *
		 * @returns a promise that resolves to true if the user is logged in and the settings are loaded, false otherwise
		 */
		async checkLoginAndSettings() {
			this.loggedIn = false;

			const mss = useMSS();
			try {
				// If no authToken is stored in localstorage, this means the user is not logged in.
				if (!localStorage.getItem('PHauthToken')) {
					return false;
				}
				let settingsUserObject = null;
				try {
					settingsUserObject = await mss.requestUserObject('globalsettings');
				} catch (error) {
					logger.error('Failure getting global settings from server, attempting to load default settings', error);
				}
				let data: GlobalSettings;
				if (settingsUserObject) {
					data = JSON.parse(settingsUserObject) as GlobalSettings;
				} else {
					data = defaultGlobalSettings;
				}
				await this.setGlobalSettings(data);

				this.loggedIn = true;
				return true;
			} catch (error) {
				logger.error('Failure to set global settings', error);
				// Remove PHauthToken and userSecret from local storage in case the enterEP did successfully return an authToken for the user
				localStorage.removeItem('PHauthToken');
				localStorage.removeItem('UserSecret');
				localStorage.removeItem('UserSecretVersion');
				return false;
			}
		},

		async setGlobalSettings(data: GlobalSettings) {
			logger.info('setGlobalSettings', data);
			const settings = useSettings();
			settings.setTheme(data.theme);
			if (!data.timeformat || (data.timeformat as string) === '') {
				data.timeformat = TimeFormat.format24;
			}
			settings.setTimeFormat(data.timeformat);
			if (!data.language || data.language === '') {
				if (settings._i18n?.locale) {
					data.language = settings._i18n?.locale.value ?? navigator.language;
				} else {
					data.language = navigator.language;
				}
			}
			settings.setLanguage(data.language);

			const mss = useMSS();
			// Check if the hubName has changed since the last update of the global settings object.
			const hubs = await mss.getHubs();
			data.hubs.forEach((hub: PinnedHub) => {
				const hubName = hubs.find((hubRespItem) => hubRespItem.id === hub.hubId)?.name;
				if (hubName) {
					hub.hubName = hubName;
				}
			});
			this.pinnedHubs = data.hubs;
		},

		login(language: string | 'en' | 'nl') {
			switch (language) {
				case 'en':
					window.location.assign(api.apiURLS.loginEn);
					break;
				case 'nl':
					window.location.assign(api.apiURLS.login);
					break;
				default:
					window.location.assign(api.apiURLS.login);
			}
		},

		async logout(message?: { key: string; values?: string[] }) {
			this.loggedIn = false;

			const mss = useMSS();
			mss.logout();

			// TODO: find a way router can be part of a store that TypeScript swallows.
			// @ts-expect-error -- router is injected as plugin, not in store type
			await this.router.replace({
				name: 'login',
				query: message ? { message: message.key, messageValues: message.values?.join(',') } : undefined,
			});
		},

		// Will be called after each relevant change in state (watched in App.vue)
		async saveGlobalSettings() {
			try {
				if (!this.loggedIn) {
					return;
				}
				const mss = useMSS();
				await mss.storeUserObject<GlobalSettings>('globalsettings', this.getGlobalSettings);
			} catch (error) {
				// @ts-expect-error -- router is injected as plugin, not in store type
				this.router.push({ name: 'error' });
				logger.error(String(error));
			}
		},

		addPinnedHub(hub: PinnedHub, order: number = -1) {
			if (!this.pinnedHubs) {
				this.pinnedHubs = [] as PinnedHubs;
			}
			// make sure the hub is flattend, we only need the hubId
			hub = { hubId: hub.hubId, hubName: hub.hubName };
			if (order < 0 || order > this.pinnedHubs.length) {
				this.pinnedHubs.push(hub);
			} else {
				this.pinnedHubs.splice(order, 0, hub);
			}
		},

		removePinnedHub(order: number) {
			this.pinnedHubs.splice(order, 1);
		},

		addAccessTokenAndUserID(hubId: string, token: string, userId: string) {
			const index = this.pinnedHubs.findIndex((hub) => hub.hubId === hubId);
			this.pinnedHubs[index].accessToken = token;
			this.pinnedHubs[index].userId = userId;
		},

		removeAccessToken(hubId: string) {
			const index = this.pinnedHubs.findIndex((hub) => hub.hubId === hubId);
			this.pinnedHubs[index].accessToken = undefined;
		},

		async getHubs() {
			this.hubsLoading = true;
			const mss = useMSS();
			const hubsStore = useHubs();
			const data = await mss.getHubs();
			const hubPromises = data.map((item) => {
				const serverUrl = item.url.replace(/\/_synapse\/client\/?$/, '/');
				return mss
					.getHubInfo(serverUrl)
					.then((hubInfo) => {
						const hub = new Hub(item.id, item.name, hubInfo.hub_client_url, serverUrl, item.description);
						// Add the hub to the store here already so an offline hub cant delay the loading of online hubs.
						hubsStore.addHub(hub);
					})
					.catch((error) => {
						logger.error(`Could not fetch hub info for hub '${item.name}' with url ${item.url}: ${error.message}`);
					});
			});
			await Promise.all(hubPromises);
			this.hubsLoading = false;
		},

		setLoadingHubs(value: boolean) {
			this.hubsLoading = value;
		},

		existsInPinnedHubs(hubId: string) {
			if (!this.pinnedHubs) return false;
			const found = this.pinnedHubs.find((hub) => hub.hubId === hubId);
			return found;
		},

		getAuthInfo(hubId: string) {
			const hub = this.pinnedHubs.find((hub) => hub.hubId === hubId);
			if (!hub) {
				return null;
			}
			const accessToken = hub.accessToken;
			const userId = hub.userId;
			if (accessToken === undefined || userId === undefined) {
				return null;
			}
			return { token: accessToken, userId };
		},

		showModal() {
			this.modalVisible = true;
		},

		hideModal() {
			this.modalVisible = false;
		},

		showContextMenuModal() {
			this.contextMenuModalVisible = true;
		},

		hideContextMenuModal() {
			this.contextMenuModalVisible = false;
		},

		setHubCanGoBack(canGoBack: boolean) {
			this.hubCanGoBack = canGoBack;
		},
	},
});

export { useGlobal, type PinnedHub, type PinnedHubs };
